import { useState, useEffect, useCallback } from "react";
import { NavLink, Outlet } from "react-router";
import { useQueueCounts } from "../hooks/useQueueCounts";
import { useAuth } from "../hooks/useAuth";
import { useCurrentTenant } from "../hooks/useCurrentTenant";
import { useWsClient } from "../lib/ws-provider";
import { useAnnouncements } from "../features/announcements/hooks/useAnnouncements";
import { AnnouncementBanner } from "../features/announcements/components/AnnouncementBanner";
import { useDirectedNotes } from "../features/directed-notes/hooks/useDirectedNotes";
import { useDirectedNoteToast } from "../features/directed-notes/hooks/useDirectedNoteToast";
import {
  canSeeAdminNav,
  canSeeAdminNavEntry,
  type AdminNavKey,
} from "./admin-nav-permissions";
import { SearchBar } from "./SearchBar";
import { ThemeToggle } from "./ThemeToggle";
import { NotificationBell } from "../features/notifications/components/NotificationBell";
import { Toaster } from "./Toaster";
import { UserMenu } from "./UserMenu";
import { SidebarGroup } from "./sidebar/SidebarGroup";
import { SidebarSubNav } from "./sidebar/SidebarSubNav";
import "./AppShell.css";

const LS_KEY = "capture-rail-pinned";
const TOAST_KEY = "capture:sidebar-reorg-toast-seen";

// Inline SVG icons (Lucide-style, 20x20)
function IconDashboard() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function IconBatches() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 19.5v-15A2.5 2.5 0 0 1 6.5 2H20v20H6.5a2.5 2.5 0 0 1 0-5H20" />
    </svg>
  );
}

function IconQueues() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="8" y1="6" x2="21" y2="6" />
      <line x1="8" y1="12" x2="21" y2="12" />
      <line x1="8" y1="18" x2="21" y2="18" />
      <line x1="3" y1="6" x2="3.01" y2="6" />
      <line x1="3" y1="12" x2="3.01" y2="12" />
      <line x1="3" y1="18" x2="3.01" y2="18" />
    </svg>
  );
}

function IconMonitor() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-testid="icon-monitor">
      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
    </svg>
  );
}

function IconAnalytics() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="20" x2="18" y2="10" />
      <line x1="12" y1="20" x2="12" y2="4" />
      <line x1="6" y1="20" x2="6" y2="14" />
    </svg>
  );
}

// Qd-Qe — sidebar icon for /history (clock + counterclockwise arrow = past/terminal docs).
function IconHistory() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-testid="icon-history">
      <path d="M3 3v5h5" />
      <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" />
      <path d="M12 7v5l4 2" />
    </svg>
  );
}

function IconSettings() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
  );
}

function IconJobTypes() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
  );
}

// C6 (ff-web) — dedicated glyph for the announcements admin nav entry
// (bullhorn), replacing the IconMonitor reuse.
function IconAnnounce() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-testid="icon-announce">
      <path d="m3 11 18-5v12L3 14v-3z" />
      <path d="M11.6 16.8a3 3 0 1 1-5.8-1.6" />
    </svg>
  );
}

// Task 11 — dedicated glyph for the always-visible /notes nav entry
// (envelope/message shape), distinct from IconPinnedNote's pushpin.
function IconDirectedNote() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-testid="icon-directed-note">
      <rect x="2" y="4" width="20" height="16" rx="2" />
      <path d="m22 6-8.97 6.65a2 2 0 0 1-2.06 0L2 6" />
    </svg>
  );
}

// C6 (ff-web) — dedicated glyph for the queue-instructions nav entry
// (pushpin, standing note), replacing the IconJobTypes reuse.
function IconPinnedNote() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-testid="icon-pinned-note">
      <line x1="12" y1="17" x2="12" y2="22" />
      <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" />
    </svg>
  );
}

// Task 7 — dedicated glyph for the /admin/teams nav entry (people/org
// chart), distinct from IconStaffMonitor's roster-card look.
function IconTeams() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-testid="icon-teams">
      <circle cx="12" cy="6" r="3" />
      <path d="M6 21v-2a4 4 0 0 1 4-4h4a4 4 0 0 1 4 4v2" />
      <path d="M2 21v-1a3 3 0 0 1 3-3" />
      <path d="M22 21v-1a3 3 0 0 1-3-3" />
    </svg>
  );
}

function IconLookupTables() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="3" y1="15" x2="21" y2="15" />
      <line x1="9" y1="9" x2="9" y2="21" />
    </svg>
  );
}

function IconRoles() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    </svg>
  );
}

function IconExport() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

function IconInputChannels() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="7" width="20" height="14" rx="2" ry="2" />
      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
    </svg>
  );
}

function IconConnections() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <ellipse cx="12" cy="5" rx="9" ry="3" />
      <path d="M21 12c0 1.66-4.03 3-9 3S3 13.66 3 12" />
      <path d="M3 5v14c0 1.66 4.03 3 9 3s9-1.34 9-3V5" />
    </svg>
  );
}

function IconLlm() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2a4 4 0 0 1 4 4c0 1.95-1.4 3.58-3.25 3.93L12 14" />
      <path d="M12 14v4" />
      <circle cx="12" cy="20" r="2" />
      <path d="M4 10c0-1.1.9-2 2-2h1" />
      <path d="M17 8h1a2 2 0 0 1 2 2" />
    </svg>
  );
}

function IconTenantSettings() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <path d="M8 21h8M12 17v4" />
    </svg>
  );
}

function IconPin() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="17" x2="12" y2="22" />
      <path d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z" />
    </svg>
  );
}

function IconPinOff() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="2" y1="2" x2="22" y2="22" />
      <line x1="12" y1="17" x2="12" y2="22" />
      <path d="M9 9v1.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V17h12" />
      <path d="M15 9.34V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v.34" />
    </svg>
  );
}

// R-236 — sidebar icon for /admin/operators (Staff Monitor — people/users glyph).
function IconStaffMonitor() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

// R-255 Phase 5d-1 — distinct icon for the Classify queue nav entry.
// Glyph: document with a magnifier/search lens — visually distinct from
// IconMonitor (waveform) used by Retention, Failed Documents, Ops Console, etc.
function IconClassify() {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      data-testid="icon-classify"
    >
      {/* Document body */}
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      {/* Magnifier lens */}
      <circle cx="10" cy="14" r="2.5" />
      <line x1="12" y1="16" x2="14.5" y2="18.5" />
    </svg>
  );
}

// R-274 7d — sidebar icon for /pending-release (upload tray + hold indicator).
function IconPendingRelease() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-testid="icon-pending-release">
      {/* tray */}
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      {/* up-arrow (staged / ready) */}
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
      {/* pause/hold dots */}
      <circle cx="8" cy="19" r="0.5" fill="currentColor" />
      <circle cx="12" cy="19" r="0.5" fill="currentColor" />
      <circle cx="16" cy="19" r="0.5" fill="currentColor" />
    </svg>
  );
}

// R-167 WU-6a — sidebar icon for /admin/counters (hash / counter glyph).
function IconCounter() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="4" y1="9" x2="20" y2="9" />
      <line x1="4" y1="15" x2="20" y2="15" />
      <line x1="10" y1="3" x2="8" y2="21" />
      <line x1="16" y1="3" x2="14" y2="21" />
    </svg>
  );
}

function IconCapture() {
  return (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 7l4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7" />
      <path d="M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8" />
      <path d="M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4" />
      <path d="M2 7h20" />
      <path d="M22 7v3a2 2 0 0 1-2 2a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 16 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 12 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 8 12a2.7 2.7 0 0 1-1.59-.63.7.7 0 0 0-.82 0A2.7 2.7 0 0 1 4 12a2 2 0 0 1-2-2V7" />
    </svg>
  );
}

export function AppShell() {
  // R-202 — consume the live WsClient from WsProvider so nav badge counts
  // refresh on queue_update WS events (the 30s poll is now just a safety net).
  const wsClient = useWsClient();
  const { totalCount: queueCount } = useQueueCounts(wsClient);
  // WU-2.1 — persistent announcement banners (fetch-on-login + live WS).
  const { announcements, dismiss: dismissAnnouncement } = useAnnouncements(wsClient);
  // Task 11 — directed-notes inbox count for the always-visible Notes nav
  // badge, plus a live toast on receipt (WU-2.3).
  const { activeCount: noteCount } = useDirectedNotes(wsClient);
  useDirectedNoteToast(wsClient);
  // F-065 — every Admin nav entry gates on the capability its surface needs.
  // The five entries that were already gated (queue-blocks, queue-instructions,
  // teams, announcements, staff-monitor) set the pattern; `can(...)` reads the
  // one declaration table in admin-nav-permissions.ts so no entry can be added
  // ungated. `showAdminNav` is derived from that same table, so the section and
  // its separator disappear entirely for a session with no admin capability;
  // an individual group whose every entry is gated off hides itself (see
  // SidebarGroup) rather than being driven from a second membership table.
  const { user } = useAuth();
  const can = useCallback(
    (key: AdminNavKey) => canSeeAdminNavEntry(user?.permissions, key),
    [user?.permissions],
  );
  const showAdminNav = canSeeAdminNav(user?.permissions);
  // F-065 part 3 — the read-only automation guide. Admins who manage rules
  // always have it; everyone else only when the tenant has opted in
  // (Tenant Settings → Operator-visible automation rules). The server is
  // authoritative — this only decides whether to offer the door.
  const { data: currentTenant } = useCurrentTenant();
  const showAutomationGuide =
    (user?.permissions?.includes("manage-rules") ?? false) ||
    (currentTenant?.operatorVisibleRules ?? false);
  const [pinned, setPinned] = useState(() => {
    try {
      return localStorage.getItem(LS_KEY) === "true";
    } catch {
      return false;
    }
  });
  const [mobileOpen, setMobileOpen] = useState(false);
  const [reorgToast, setReorgToast] = useState<boolean>(() => !localStorage.getItem(TOAST_KEY));

  const togglePin = useCallback(() => {
    setPinned((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(LS_KEY, String(next));
      } catch { /* noop */ }
      return next;
    });
  }, []);

  useEffect(() => {
    if (reorgToast) {
      const t = setTimeout(() => {
        localStorage.setItem(TOAST_KEY, '1');
        setReorgToast(false);
      }, 8000);
      return () => clearTimeout(t);
    }
  }, [reorgToast]);

  // Close mobile nav on Escape
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && mobileOpen) setMobileOpen(false);
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  const railClass = [
    "rail",
    pinned ? "rail--pinned" : "",
    mobileOpen ? "rail--mobile-open" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const contentClass = [
    "app-shell-content",
    pinned ? "app-shell-content--pinned" : "",
  ]
    .filter(Boolean)
    .join(" ");

  const closeMobile = () => setMobileOpen(false);

  return (
    <div className="app-shell form-default">
      {/* Mobile backdrop */}
      {mobileOpen && (
        <div className="mobile-backdrop" onClick={closeMobile} />
      )}

      {/* Command Rail */}
      <nav className={railClass} aria-label="Main navigation">
        {/* Mark area */}
        <div className="rail-mark">
          <div className="rail-mark-icon">
            <IconCapture />
          </div>
          <span className="rail-mark-text">Capture</span>
          <button
            className="rail-pin-btn"
            onClick={togglePin}
            aria-label={pinned ? "Unpin sidebar" : "Pin sidebar open"}
          >
            {pinned ? <IconPinOff /> : <IconPin />}
          </button>
        </div>

        {/* Nav items */}
        <div className="rail-nav">
          <NavLink
            to="/dashboard"
            className={({ isActive }) =>
              `rail-nav-item ${isActive ? "active" : ""}`
            }
            onClick={closeMobile}
          >
            <span className="rail-nav-icon"><IconDashboard /></span>
            <span className="rail-nav-label">Dashboard</span>
          </NavLink>

          <NavLink
            to="/batches"
            className={({ isActive }) =>
              `rail-nav-item ${isActive ? "active" : ""}`
            }
            onClick={closeMobile}
          >
            <span className="rail-nav-icon"><IconBatches /></span>
            <span className="rail-nav-label">Batches</span>
          </NavLink>

          <NavLink
            to="/queues"
            className={({ isActive }) =>
              `rail-nav-item ${isActive ? "active" : ""}`
            }
            onClick={closeMobile}
          >
            <span className="rail-nav-icon"><IconQueues /></span>
            <span className="rail-nav-label">Queues</span>
            {queueCount > 0 && (
              <span className="rail-nav-badge" data-testid="queue-badge">{queueCount}</span>
            )}
          </NavLink>

          {/* Task 11 — always-visible directed-notes inbox (not gated on a
              permission — every operator can receive a note). */}
          <NavLink
            to="/notes"
            className={({ isActive }) =>
              `rail-nav-item ${isActive ? "active" : ""}`
            }
            onClick={closeMobile}
          >
            <span className="rail-nav-icon"><IconDirectedNote /></span>
            <span className="rail-nav-label">Notes</span>
            {noteCount > 0 && (
              <span className="rail-nav-badge" data-testid="notes-badge">{noteCount}</span>
            )}
          </NavLink>

          {/* F-065 — read-only automation guide. Operator-facing (above the
              admin separator) because its whole point is that some tenants
              want operators to read it. */}
          {showAutomationGuide && (
            <NavLink
              to="/rules-guide"
              className={({ isActive }) =>
                `rail-nav-item ${isActive ? "active" : ""}`
              }
              onClick={closeMobile}
            >
              <span className="rail-nav-icon"><IconPinnedNote /></span>
              <span className="rail-nav-label">Automation Guide</span>
            </NavLink>
          )}

          {/* F-065 — the Admin section (separator included) renders only when
              the session holds at least one admin capability. An Operator's
              rail ends at Notes. */}
          {showAdminNav && (
          <>
          {/* Separator */}
          <div className="rail-separator" />

          {/* Admin section — 5 grouped categories (R-3) */}
          <nav className="app-shell__nav">
            <SidebarGroup label="Forms" persistKey="forms">
              {can("jobTypes") && (
                <NavLink
                  to="/admin/job-types"
                  className={({ isActive }) =>
                    `rail-nav-item ${isActive ? "active" : ""}`
                  }
                  onClick={closeMobile}
                >
                  <span className="rail-nav-icon"><IconJobTypes /></span>
                  <span className="rail-nav-label">Job Types</span>
                </NavLink>
              )}
            </SidebarGroup>

            <SidebarGroup label="Operations" persistKey="operations">
              {can("failedDocuments") && (
                <NavLink
                  to="/admin/failed-documents"
                  className={({ isActive }) =>
                    `rail-nav-item ${isActive ? "active" : ""}`
                  }
                  onClick={closeMobile}
                >
                  <span className="rail-nav-icon"><IconMonitor /></span>
                  <span className="rail-nav-label">Failed Documents</span>
                </NavLink>
              )}
              {can("opsConsole") && (
                <NavLink
                  to="/ops"
                  className={({ isActive }) =>
                    `rail-nav-item ${isActive ? "active" : ""}`
                  }
                  onClick={closeMobile}
                >
                  <span className="rail-nav-icon"><IconMonitor /></span>
                  <span className="rail-nav-label">Ops Console</span>
                </NavLink>
              )}
              {can("ocrHealth") && (
                <NavLink
                  to="/admin/ocr-health"
                  className={({ isActive }) =>
                    `rail-nav-item ${isActive ? "active" : ""}`
                  }
                  onClick={closeMobile}
                >
                  <span className="rail-nav-icon"><IconMonitor /></span>
                  <span className="rail-nav-label">OCR Health</span>
                </NavLink>
              )}
              {/* R-167 WU-6a — admin page for managing per-tenant / per-job-type
                  custom auto-increment counters referenced by display
                  templates (`{counter:NAME}`). Gated server-side on the
                  `manage:custom-counters` permission. */}
              {can("customCounters") && (
                <NavLink
                  to="/admin/counters"
                  className={({ isActive }) =>
                    `rail-nav-item ${isActive ? "active" : ""}`
                  }
                  onClick={closeMobile}
                >
                  <span className="rail-nav-icon"><IconCounter /></span>
                  <span className="rail-nav-label">Custom Counters</span>
                </NavLink>
              )}
              {/* R-162 §10.1 — supervisor queue-block admin. Gated on
                  `supervisor.queue_block`; hidden for operators who can't
                  manage blocks (the route 403s for them anyway). */}
              {can("queueBlocks") && (
                <NavLink
                  to="/admin/queue-blocks"
                  className={({ isActive }) =>
                    `rail-nav-item ${isActive ? "active" : ""}`
                  }
                  onClick={closeMobile}
                >
                  <span className="rail-nav-icon"><IconMonitor /></span>
                  <span className="rail-nav-label">Queue Blocks</span>
                </NavLink>
              )}
              {/* WU-2.2 — supervisor queue-instructions admin. Gated on
                  `supervisor.queue_instruct`. */}
              {can("queueInstructions") && (
                <NavLink
                  to="/admin/queue-instructions"
                  className={({ isActive }) =>
                    `rail-nav-item ${isActive ? "active" : ""}`
                  }
                  onClick={closeMobile}
                >
                  <span className="rail-nav-icon"><IconPinnedNote /></span>
                  <span className="rail-nav-label">Queue Instructions</span>
                </NavLink>
              )}
              {/* Task 7 — Team Hierarchy T1 builder page. Gated on team.manage. */}
              {can("teams") && (
                <NavLink
                  to="/admin/teams"
                  className={({ isActive }) =>
                    `rail-nav-item ${isActive ? "active" : ""}`
                  }
                  onClick={closeMobile}
                >
                  <span className="rail-nav-icon"><IconTeams /></span>
                  <span className="rail-nav-label">Teams</span>
                </NavLink>
              )}
              {/* WU-2.1 — announcements manager. Gated on supervisor.announce. */}
              {can("announcements") && (
                <NavLink
                  to="/admin/announcements"
                  className={({ isActive }) =>
                    `rail-nav-item ${isActive ? "active" : ""}`
                  }
                  onClick={closeMobile}
                >
                  <span className="rail-nav-icon"><IconAnnounce /></span>
                  <span className="rail-nav-label">Announcements</span>
                </NavLink>
              )}
              {/* Task 14 (WU-2.3) — supervisor sent-notes admin page. Gated
                  on canSendDirectNote (supervisor.direct_note OR admin). */}
              {can("directedNotes") && (
                <NavLink
                  to="/admin/directed-notes"
                  className={({ isActive }) =>
                    `rail-nav-item ${isActive ? "active" : ""}`
                  }
                  onClick={closeMobile}
                >
                  <span className="rail-nav-icon"><IconDirectedNote /></span>
                  <span className="rail-nav-label">Directed Notes</span>
                </NavLink>
              )}
              {/* R-236 — Staff Monitor: live roster + historical stats for supervisors. */}
              {can("staffMonitor") && (
                <NavLink
                  to="/admin/operators"
                  className={({ isActive }) =>
                    `rail-nav-item ${isActive ? "active" : ""}`
                  }
                  onClick={closeMobile}
                >
                  <span className="rail-nav-icon"><IconStaffMonitor /></span>
                  <span className="rail-nav-label">Staff Monitor</span>
                </NavLink>
              )}
              {/* R-244 — Post-export retention queue */}
              {can("retention") && (
                <NavLink
                  to="/ops/retention"
                  className={({ isActive }) =>
                    `rail-nav-item ${isActive ? "active" : ""}`
                  }
                  onClick={closeMobile}
                >
                  <span className="rail-nav-icon"><IconMonitor /></span>
                  <span className="rail-nav-label">Retention</span>
                </NavLink>
              )}
              {/* Qd-Qe — document history (awaiting-purge / purged + reprocess) */}
              {can("history") && (
                <NavLink
                  to="/history"
                  className={({ isActive }) =>
                    `rail-nav-item ${isActive ? "active" : ""}`
                  }
                  onClick={closeMobile}
                >
                  <span className="rail-nav-icon"><IconHistory /></span>
                  <span className="rail-nav-label">History</span>
                </NavLink>
              )}
              {/* R-245 — Classification queue (parked docs awaiting type) */}
              {/* R-255 5d-1: uses IconClassify (document+magnifier), not IconMonitor */}
              {can("classify") && (
                <NavLink
                  to="/ops/classify"
                  className={({ isActive }) =>
                    `rail-nav-item ${isActive ? "active" : ""}`
                  }
                  onClick={closeMobile}
                >
                  <span className="rail-nav-icon"><IconClassify /></span>
                  <span className="rail-nav-label">Classify</span>
                </NavLink>
              )}
              {/* R-274 7d — bundles held for manual release before delivery */}
              {can("pendingRelease") && (
                <NavLink
                  to="/pending-release"
                  className={({ isActive }) =>
                    `rail-nav-item ${isActive ? "active" : ""}`
                  }
                  onClick={closeMobile}
                >
                  <span className="rail-nav-icon"><IconPendingRelease /></span>
                  <span className="rail-nav-label">Pending Release</span>
                </NavLink>
              )}
            </SidebarGroup>

            <SidebarGroup label="Data" persistKey="data">
              {can("lookupTables") && (
                <NavLink
                  to="/admin/lookup-tables"
                  className={({ isActive }) =>
                    `rail-nav-item ${isActive ? "active" : ""}`
                  }
                  onClick={closeMobile}
                >
                  <span className="rail-nav-icon"><IconLookupTables /></span>
                  <span className="rail-nav-label">Lookup Tables</span>
                </NavLink>
              )}
              {can("namedChains") && (
                <NavLink
                  to="/admin/named-chains"
                  className={({ isActive }) =>
                    `rail-nav-item ${isActive ? "active" : ""}`
                  }
                  onClick={closeMobile}
                >
                  <span className="rail-nav-icon"><IconLookupTables /></span>
                  <span className="rail-nav-label">Named Chains</span>
                </NavLink>
              )}
              {can("transformPlugins") && (
                <NavLink
                  to="/admin/transform-plugins"
                  className={({ isActive }) =>
                    `rail-nav-item ${isActive ? "active" : ""}`
                  }
                  onClick={closeMobile}
                >
                  <span className="rail-nav-icon"><IconSettings /></span>
                  <span className="rail-nav-label">Transform Plugins</span>
                </NavLink>
              )}
            </SidebarGroup>

            <SidebarGroup label="Integrations" persistKey="integrations">
              {can("inputChannels") && (
                <NavLink
                  to="/admin/input-channels"
                  className={({ isActive }) =>
                    `rail-nav-item ${isActive ? "active" : ""}`
                  }
                  onClick={closeMobile}
                >
                  <span className="rail-nav-icon"><IconInputChannels /></span>
                  <span className="rail-nav-label">Input Channels</span>
                </NavLink>
              )}
              {can("exportFormats") && (
                <NavLink
                  to="/admin/export-formats"
                  className={({ isActive }) =>
                    `rail-nav-item ${isActive ? "active" : ""}`
                  }
                  onClick={closeMobile}
                >
                  <span className="rail-nav-icon"><IconExport /></span>
                  <span className="rail-nav-label">Export Formats</span>
                </NavLink>
              )}
              {can("exportHistory") && (
                <NavLink
                  to="/admin/export-history"
                  className={({ isActive }) =>
                    `rail-nav-item ${isActive ? "active" : ""}`
                  }
                  onClick={closeMobile}
                >
                  <span className="rail-nav-icon"><IconExport /></span>
                  <span className="rail-nav-label">Export History</span>
                </NavLink>
              )}
              {can("outputs") && (
                <NavLink
                  to="/admin/outputs"
                  className={({ isActive }) =>
                    `rail-nav-item ${isActive ? "active" : ""}`
                  }
                  onClick={closeMobile}
                >
                  <span className="rail-nav-icon"><IconExport /></span>
                  <span className="rail-nav-label">Outputs Catalog</span>
                </NavLink>
              )}
              {can("connections") && (
                <NavLink
                  to="/admin/connections"
                  className={({ isActive }) =>
                    `rail-nav-item ${isActive ? "active" : ""}`
                  }
                  onClick={closeMobile}
                >
                  <span className="rail-nav-icon"><IconConnections /></span>
                  <span className="rail-nav-label">Connections</span>
                </NavLink>
              )}
              {can("agents") && (
                <NavLink
                  to="/admin/agents"
                  className={({ isActive }) =>
                    `rail-nav-item ${isActive ? "active" : ""}`
                  }
                  onClick={closeMobile}
                >
                  <span className="rail-nav-icon"><IconConnections /></span>
                  <span className="rail-nav-label">Connected Agents</span>
                </NavLink>
              )}
            </SidebarGroup>

            <SidebarGroup label="System" persistKey="system">
              {(can("llm") || can("llmUsage")) && (
                <SidebarSubNav parentTo="/admin/llm" parentLabel="LLM">
                  {can("llm") && (
                    <NavLink
                      to="/admin/llm"
                      className={({ isActive }) =>
                        `rail-nav-item ${isActive ? "active" : ""}`
                      }
                      onClick={closeMobile}
                    >
                      <span className="rail-nav-icon"><IconLlm /></span>
                      <span className="rail-nav-label">Settings</span>
                    </NavLink>
                  )}
                  {can("llmUsage") && (
                    <NavLink
                      to="/admin/llm/usage"
                      className={({ isActive }) =>
                        `rail-nav-item ${isActive ? "active" : ""}`
                      }
                      onClick={closeMobile}
                    >
                      <span className="rail-nav-icon"><IconAnalytics /></span>
                      <span className="rail-nav-label">Usage</span>
                    </NavLink>
                  )}
                </SidebarSubNav>
              )}
              {can("roles") && (
                <NavLink
                  to="/admin/roles"
                  className={({ isActive }) =>
                    `rail-nav-item ${isActive ? "active" : ""}`
                  }
                  onClick={closeMobile}
                >
                  <span className="rail-nav-icon"><IconRoles /></span>
                  <span className="rail-nav-label">Roles</span>
                </NavLink>
              )}
              {can("tenantSettings") && (
                <NavLink
                  to="/admin/settings"
                  className={({ isActive }) =>
                    `rail-nav-item ${isActive ? "active" : ""}`
                  }
                  onClick={closeMobile}
                >
                  <span className="rail-nav-icon"><IconTenantSettings /></span>
                  <span className="rail-nav-label">Tenant Settings</span>
                </NavLink>
              )}
              {can("reports") && (
                <NavLink
                  to="/admin/reports"
                  className={({ isActive }) =>
                    `rail-nav-item ${isActive ? "active" : ""}`
                  }
                  onClick={closeMobile}
                >
                  <span className="rail-nav-icon"><IconAnalytics /></span>
                  <span className="rail-nav-label">Reports</span>
                </NavLink>
              )}
              {can("analytics") && (
                <NavLink
                  to="/admin/analytics"
                  className={({ isActive }) =>
                    `rail-nav-item ${isActive ? "active" : ""}`
                  }
                  onClick={closeMobile}
                >
                  <span className="rail-nav-icon"><IconAnalytics /></span>
                  <span className="rail-nav-label">Analytics</span>
                </NavLink>
              )}
            </SidebarGroup>
          </nav>
          </>
          )}
        </div>
      </nav>

      {/* Main content area */}
      <main className={contentClass}>
        <div className="app-shell-header">
          <SearchBar />
          <NotificationBell wsClient={wsClient} />
          <ThemeToggle />
          <UserMenu />
        </div>
        <AnnouncementBanner announcements={announcements} onDismiss={(id) => void dismissAnnouncement(id)} />
        <Outlet />
      </main>
      <Toaster />
      {reorgToast && (
        <div
          className="app-shell__reorg-toast"
          role="status"
          onClick={() => { localStorage.setItem(TOAST_KEY, '1'); setReorgToast(false); }}
        >
          We've reorganized the admin sidebar — find old links under their new group.
        </div>
      )}
    </div>
  );
}
